import {
  postOpsAnalyticsAPI,
  postOpsAnalyticsBlobAPI,
} from "../../../../api/Networking";
import { APIBlobResponse } from "../../../../models/NetworkResponse";
import {
  AlertFilters,
  AlertsDrilldownRequest,
  AlertsEmailListRequest,
  AlertsPushListRequest,
  AlertsRecipientRequest,
} from "../../../../models/Dashboard/AlertsDashboard/AlertsDashboardFilters";
import {
  AlertsAlertDetailResult,
  AlertsDrilldownResult,
  AlertsEmailBatchDetailResult,
  AlertsListResult,
  AlertsRecipientResult,
} from "../../../../models/Dashboard/AlertsDashboard/AlertsDashboardDrilldown";
import { AlertsSummaryResult } from "../../../../models/Dashboard/AlertsDashboard/AlertsDashboardSummary";

const BASE_PATH = "/v1/dashboards/alerts";

/**
 * Posts to the analytics host and unwraps `{ success, message, result }`.
 * Throws with the server `message` so the store can show it inline.
 */
const postResult = async <T>(
  path: string,
  body: unknown,
  fallback: string,
): Promise<T> => {
  try {
    const response = await postOpsAnalyticsAPI(`${BASE_PATH}${path}`, body);
    if (!response.success || !response.result) {
      throw new Error(response.message || fallback);
    }
    return response.result as T;
  } catch (error) {
    throw error;
  }
};

/** Thin wrapper over the Alerts monitoring dashboard endpoints (all `POST`). */
export default class AlertsDashboardAPI {
  getSummary = async (filters: AlertFilters): Promise<AlertsSummaryResult> =>
    postResult<AlertsSummaryResult>(
      "/summary",
      filters,
      "Failed to fetch alerts summary.",
    );

  getDrilldown = async (
    metricId: string,
    body: AlertsDrilldownRequest,
  ): Promise<AlertsDrilldownResult> =>
    postResult<AlertsDrilldownResult>(
      `/drilldown/${encodeURIComponent(metricId)}`,
      body,
      "Failed to fetch alerts drill-down.",
    );

  getAlertDetail = async (alertId: number): Promise<AlertsAlertDetailResult> =>
    postResult<AlertsAlertDetailResult>(
      `/alert/${alertId}`,
      {},
      "Failed to fetch alert detail.",
    );

  getEmails = async (body: AlertsEmailListRequest): Promise<AlertsListResult> =>
    postResult<AlertsListResult>(
      "/emails",
      body,
      "Failed to fetch alert emails.",
    );

  getEmailBatch = async (
    batchId: number,
  ): Promise<AlertsEmailBatchDetailResult> =>
    postResult<AlertsEmailBatchDetailResult>(
      `/emails/${batchId}`,
      {},
      "Failed to fetch alert email.",
    );

  getPush = async (body: AlertsPushListRequest): Promise<AlertsListResult> =>
    postResult<AlertsListResult>(
      "/push",
      body,
      "Failed to fetch alert push deliveries.",
    );

  getRecipient = async (
    body: AlertsRecipientRequest,
  ): Promise<AlertsRecipientResult> =>
    postResult<AlertsRecipientResult>(
      "/recipient",
      body,
      "Failed to fetch alert recipient.",
    );

  exportDrilldownCsv = async (
    metricId: string,
    body: AlertsDrilldownRequest,
  ): Promise<APIBlobResponse> => {
    try {
      return await postOpsAnalyticsBlobAPI(
        `${BASE_PATH}/drilldown/${encodeURIComponent(metricId)}`,
        { ...body, format: "csv" },
      );
    } catch (error) {
      throw error;
    }
  };

  exportEmailsCsv = async (
    body: AlertsEmailListRequest,
  ): Promise<APIBlobResponse> => {
    try {
      return await postOpsAnalyticsBlobAPI(`${BASE_PATH}/emails`, {
        ...body,
        format: "csv",
      });
    } catch (error) {
      throw error;
    }
  };

  exportPushCsv = async (
    body: AlertsPushListRequest,
  ): Promise<APIBlobResponse> => {
    try {
      return await postOpsAnalyticsBlobAPI(`${BASE_PATH}/push`, {
        ...body,
        format: "csv",
      });
    } catch (error) {
      throw error;
    }
  };

  exportRecipientCsv = async (
    body: AlertsRecipientRequest,
  ): Promise<APIBlobResponse> => {
    try {
      return await postOpsAnalyticsBlobAPI(`${BASE_PATH}/recipient`, {
        ...body,
        format: "csv",
      });
    } catch (error) {
      throw error;
    }
  };
}
